import { Textarea } from '@ibsys/kit';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { useUpdateAnonymizerConfig, useAnonymizerConfig } from '@/entities/anonymizer-config';
import type { BirthDateConfig } from '@/entities/anonymizer-config';
import { ConfigFormLayout } from './ConfigFormLayout';

type FormValues = {
  datePatternRegex: string;
  contextKeywordRegex: string;
  contextMarkers: string;
  lookbackWindow: string;
  currentYear: string;
};

const getDefaultValues = (config: BirthDateConfig | undefined): FormValues => ({
  datePatternRegex: config?.datePatternRegex ?? '',
  contextKeywordRegex: config?.contextKeywordRegex ?? '',
  contextMarkers: config?.contextMarkers?.join('\n') ?? '',
  lookbackWindow: String(config?.lookbackWindow ?? ''),
  currentYear: String(config?.currentYear ?? ''),
});

type Props = {
  data: unknown;
  onUnsavedChange?: (dirty: boolean) => void;
};

export const BirthDateConfigForm: FC<Props> = ({ data, onUnsavedChange }) => {
  const { t } = useTranslation('anonymizerConfig');
  const canEdit = useHasPermission('ADMIN_ANONYMIZE_CONFIG');
  const config = data as BirthDateConfig | undefined;
  const { mutate: updateConfig, isPending } = useUpdateAnonymizerConfig();
  const { refetch } = useAnonymizerConfig('birth-date');

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: getDefaultValues(config),
  });

  useEffect(() => {
    if (config) {
      reset(getDefaultValues(config));
    }
  }, [config, reset]);

  useEffect(() => {
    onUnsavedChange?.(isDirty);
  }, [isDirty, onUnsavedChange]);

  const onSubmit = handleSubmit((values) => {
    if (!config) return;
    const body: BirthDateConfig = {
      datePatternRegex: values.datePatternRegex || config.datePatternRegex,
      contextKeywordRegex: values.contextKeywordRegex || config.contextKeywordRegex,
      contextMarkers: values.contextMarkers ? values.contextMarkers.split('\n').filter(Boolean) : config.contextMarkers,
      lookbackWindow: values.lookbackWindow ? Number(values.lookbackWindow) : config.lookbackWindow,
      currentYear: values.currentYear ? Number(values.currentYear) : config.currentYear,
    };
    updateConfig({ className: 'birth-date', body });
  });

  const handleReset = () => {
    reset(getDefaultValues(config));
    refetch();
  };

  return (
    <ConfigFormLayout
      title={t('classes.birthDate')}
      isDirty={isDirty}
      onSave={onSubmit}
      onReset={handleReset}
      isSaving={isPending}
    >
      <div className="flex flex-col gap-6 [&_label]:text-foreground">
        <Textarea
          control={control}
          name="datePatternRegex"
          label={t('fields.datePatternRegex')}
          disabled={!canEdit}
          rows={3}
        />
        <Textarea
          control={control}
          name="contextKeywordRegex"
          label={t('fields.contextKeywordRegex')}
          disabled={!canEdit}
          rows={3}
        />
        <Textarea
          control={control}
          name="contextMarkers"
          label={t('fields.contextMarkers')}
          disabled={!canEdit}
          rows={5}
        />
        <Textarea
          control={control}
          name="lookbackWindow"
          label={t('fields.lookbackWindow')}
          disabled={!canEdit}
          rows={1}
        />
        <Textarea control={control} name="currentYear" label={t('fields.currentYear')} disabled={!canEdit} rows={1} />
      </div>
    </ConfigFormLayout>
  );
};
